import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { InlineNotification, Search, Tag, TreeNode, TreeView } from '@carbon/react';
import { api } from '../api/client';
import { BlockSkeleton, EmptyState, ErrorState, PageHeader } from '../components/states';

type Node = {
  id: string; code: string; displayName: string; parentId: string | null; status: string;
  commissionRateBps: number; isSuperRoot: boolean; directMembers: number;
};
type Tree = { items: Node[]; truncated: boolean };

const ST: Record<string, string> = { ACTIVE: 'Hoạt động', PAUSED: 'Tạm dừng', BANNED: 'Khóa' };

export function AgentTreePage() {
  const q = useQuery({ queryKey: ['agent-tree'], queryFn: () => api.get<Tree>('/api/admin/agents/tree') });
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string | null>(null);

  const { roots, kids, byId } = useMemo(() => {
    const items = q.data?.items ?? [];
    const byId = new Map(items.map((n) => [n.id, n]));
    const kids = new Map<string, Node[]>();
    const roots: Node[] = [];
    for (const n of items) {
      // a parent outside the loaded set (or none) makes the node a root, so nothing is hidden
      if (n.parentId && byId.has(n.parentId)) kids.set(n.parentId, [...(kids.get(n.parentId) ?? []), n]);
      else roots.push(n);
    }
    return { roots, kids, byId };
  }, [q.data]);

  // search keeps the matching agents and every agent above them, expanded
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return null;
    const keep = new Set<string>();
    for (const n of byId.values()) {
      if (!n.code.includes(s) && !n.displayName.toLowerCase().includes(s)) continue;
      for (let c: Node | undefined = n; c && !keep.has(c.id); c = c.parentId ? byId.get(c.parentId) : undefined) keep.add(c.id);
    }
    return keep;
  }, [search, byId]);

  const subtree = (id: string): number => (kids.get(id) ?? []).reduce((a, k) => a + 1 + subtree(k.id), 0);

  const render = (n: Node, depth: number): ReactNode => {
    if (shown && !shown.has(n.id)) return null;
    const children = kids.get(n.id) ?? [];
    const label = (
      <span className="tree-agent">
        <span className="num">{n.code}</span>
        <span>{n.displayName}</span>
        <span className="muted"><span className="num">{n.commissionRateBps}</span> bps · <span className="num">{n.directMembers}</span> thành viên</span>
        {n.status !== 'ACTIVE' ? <Tag size="sm" type="red">{ST[n.status] ?? n.status}</Tag> : null}
      </span>
    );
    return (
      <TreeNode key={n.id} id={n.id} value={n.id} label={label} isExpanded={!!shown || depth < 2}
        onSelect={() => setPicked(n.id)}>
        {children.length ? children.map((c) => render(c, depth + 1)) : undefined}
      </TreeNode>
    );
  };

  const sel = picked ? byId.get(picked) : undefined;
  return (
    <div className="page">
      <PageHeader title="Cây đại lý" lead="Nhà cái ở gốc, mỗi nhánh là tuyến dưới của một đại lý. Chọn một đại lý để xem tóm tắt." />
      <div className="toolbar-filters">
        <div className="toolbar-filters__grow">
          <Search id="tree-s" size="md" labelText="Tìm trong cây" placeholder="Mã hoặc tên đại lý" value={search}
            onChange={(e) => setSearch(e.target.value)} onClear={() => setSearch('')} />
        </div>
      </div>
      {q.data?.truncated ? (
        <InlineNotification kind="warning" lowContrast hideCloseButton title="Cây quá lớn"
          subtitle="Chỉ hiện 5.000 đại lý đầu tiên; tìm theo mã để thu hẹp." />
      ) : null}
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : roots.length === 0 ? (
        <EmptyState title="Chưa có đại lý" body="Kích đại lý từ trang Đại lý hoặc từ hồ sơ người chơi." />
      ) : shown && shown.size === 0 ? (
        <EmptyState title="Không có đại lý khớp" body="Thử mã hoặc tên khác." />
      ) : (
        <div className="split split--tree">
          <TreeView label="Cây đại lý" hideLabel size="sm" active={picked ?? undefined}>
            {roots.map((r) => render(r, 0))}
          </TreeView>
          {sel ? (
            <section className="panel" aria-label="Đại lý đang chọn">
              <h2>{sel.displayName} · <span className="num">{sel.code}</span></h2>
              <p className="muted">{sel.isSuperRoot ? 'Nhà cái' : 'Tuyến trên ' + (sel.parentId ? byId.get(sel.parentId)?.code ?? '-' : '-')}</p>
              <dl className="facts">
                <dt>Tỷ lệ</dt><dd><span className="num">{sel.commissionRateBps}</span> bps</dd>
                <dt>Thành viên trực tiếp</dt><dd className="num">{sel.directMembers}</dd>
                <dt>Đại lý cấp dưới (cả nhánh)</dt><dd className="num">{subtree(sel.id)}</dd>
                <dt>Trạng thái</dt><dd>{ST[sel.status] ?? sel.status}</dd>
              </dl>
              <Link to={'/agents/' + sel.id}>Mở trang đại lý</Link>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}
